import { useGSAP } from "@gsap/react";
import { Check } from "lucide-react";
import Button from "../../components/common/Button";
import { useAuthStore } from "../../store/authStore.js";
import { animateWithGsap } from "../../utils/animations";
import SubscriptionPricingCard from "./SubscriptionPricingCard";

const SubscriptionHeader = () => {
  const user = useAuthStore(s => s.user);
  useGSAP(() => {
    animateWithGsap(".g-title", {
      y: 0,
      opacity: 1,
      ease: "power2.inOut",
      duration: 1,
      stagger: 0.2,
    })
    animateWithGsap(".g-aviation-features", {
      y: 0,
      opacity: 1,
      ease: "power2.inOut",
      duration: 1,
      stagger: 0.2,
    }, {
      start: 'top 65%'
    })
  }, [])

  const headerContent = {
    professional: {
      title: "Boost Your Aviation Career",
      description: "Choose the plan that fits your job search needs and land your dream aviation role faster"
    },
    recruiter: {
      title: "Find Top Aviation Talent",
      description: "Select the perfect plan to connect with qualified aviation professionals and fill positions faster"
    },
    default: {
      title: "Unlock Premium Features",
      description: "Choose a subscription plan to enhance your aviation industry experience"
    }
  };

  const role = user?.role || 'default';
  const content = headerContent[role] || headerContent.default;

  return (
    <div className="text-center mb-12">
      <h2 className="text-4xl font-bold text-gray-900 mb-4 g-title opacity-0 translate-y-[100px]">
        {content.title}
      </h2>
      <p className="text-xl text-gray-600 max-w-2xl mx-auto g-title opacity-0 translate-y-[100px]">
        {content.description}
      </p>
    </div>
  );
};

const TrustedBySection = () => {
  return (
    <div className="bg-white py-4">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 g-title opacity-0 translate-y-[100px]">
            Trusted by Aviation Professionals Worldwide
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto g-title opacity-0 translate-y-[100px]">
          <div className="text-center">
            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Bank-Level Security</h3>
            <p className="text-gray-600">Your data is protected with enterprise-grade encryption</p>
          </div>

          <div className="text-center">
            <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Support</h3>
            <p className="text-gray-600">Aviation industry experts available to help 24/7</p>
          </div>

          <div className="text-center">
            <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Global Network</h3>
            <p className="text-gray-600">Connecting talent across 45+ countries worldwide</p>
          </div>

          <div className="text-center">
            <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Industry Leading</h3>
            <p className="text-gray-600">4.9/5 rating from 10,000+ aviation professionals</p>
          </div>
        </div>

        {/* Call to Action */}
        {/* <div className="text-center mt-16 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Take Your Aviation Career to New Heights?
          </h3>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
            Join thousands of aviation professionals who have advanced their careers with WinguPort. 
            Start your trial today and experience the difference.
          </p>
          <div className="border-t border-gray-200 pt-6 mb-6">
            <p className="text-lg font-semibold text-gray-900 mb-4">Start Your Trial Today</p>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg text-lg">
              Get Started Now
            </Button>
          </div>
        </div> */}
      </div>
    </div>
  );
};

const AviationFeatures = () => {
  return (
    <div className="bg-gray-50 py-4">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 g-aviation-features opacity-0 translate-y-[100px]">
            Why Aviation Professionals Choose WinguPort
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto g-aviation-features opacity-0 translate-y-[100px]">
            Purpose-built for the aviation industry with features that understand your career needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto g-aviation-features opacity-0 translate-y-[100px]">
          <div className="text-center">
            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Verified Profiles</h3>
            <p className="text-gray-600">License and certification verification for credibility</p>
          </div>

          <div className="text-center">
            <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Aircraft Type Matching</h3>
            <p className="text-gray-600">Find opportunities specific to your aircraft experience</p>
          </div>

          <div className="text-center">
            <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Career Growth</h3>
            <p className="text-gray-600">Tools and insights to advance your aviation career</p>
          </div>

          <div className="text-center">
            <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Global Reach</h3>
            <p className="text-gray-600">Connect with opportunities in 45+ countries</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomSolutionSection = () => {
  return (
    <div className="bg-gray-900 py-10 mx-4 my-4 rounded-lg">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Need a Custom Solution?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Looking for enterprise-level features, custom integrations, or volume discounts?
            Let's discuss a tailored solution for your aviation organization.
          </p>
          <Button className="bg-teal-50 hover:bg-gray-300 !text-gray-900 font-semibold py-3 px-8 rounded-lg">
            Contact Sales
          </Button>
        </div>
      </div>
    </div>
  );
};

const SubscriptionPricingSection = ({ plans, onSubscribe, showFreePlan = false }) => {
  return (
    <div>

      {/* Pricing Section */}
      <div className="bg-secondary-50 py-4">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Section Header */}
          <SubscriptionHeader />

          {/* Pricing Cards */}
          <div className={`grid gap-8 max-w-6xl mx-auto ${plans.length === 1 ? 'grid-cols-1 max-w-md' :
              plans.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
            {plans.map((plan, index) => (
              <SubscriptionPricingCard
                key={plan.id}
                plan={plan}
                onSubscribe={onSubscribe}
              />
            ))}

            {/* Optional Free Plan */}
            {showFreePlan && (
              <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6 flex flex-col">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
                  <p className="text-gray-600 text-sm">Basic job search features</p>
                </div>

                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-gray-900">Ksh0</span>
                    <span className="text-gray-500 text-lg">/month</span>
                  </div>
                </div>

                <div className="space-y-3 mb-8 flex-1">
                  <div className="flex items-center gap-3">
                    <div className="p-1 rounded-full bg-gray-100 text-gray-600">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 text-sm">Basic Job Search</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-1 rounded-full bg-gray-100 text-gray-600">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 text-sm">5 Job Applications</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-1 rounded-full bg-gray-100 text-gray-600">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 text-sm">Community Support</span>
                  </div>
                </div>

                <Button
                  variant="secondary"
                  fullWidth
                  className="py-3 font-semibold text-base"
                >
                  Continue Free
                </Button>
              </div>
            )}
          </div>

          {/* Additional Info */}
          {/* <div className="text-center mt-12">
            <p className="text-gray-600 text-sm">
              All plans include access to our aviation industry network and job matching algorithm
            </p>
          </div> */}
        </div>
      </div>

      {/* Aviation Features Section */}
      <AviationFeatures />

      {/* Custom Solution Section */}
      <CustomSolutionSection />

      {/* Trusted By Section */}
      <TrustedBySection />
    </div>
  );
};

export default SubscriptionPricingSection;